/**
 * =========================================
 * FUNCIONES DE FECHA - REPÚBLICA DOMINICANA
 * Zona horaria: UTC-4 (AST)
 * Formato obligatorio: dd/mm/yyyy HH:MM a. m./p. m.
 * =========================================
 */

/**
 * Obtiene la fecha actual en zona horaria de República Dominicana (UTC-4)
 * @returns {Date} Fecha ajustada a UTC-4
 */
export const getDateInDR = (date = new Date()) => {
  // Convertir a zona horaria de República Dominicana (UTC-4)
  const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
  const drOffset = -4 * 60 * 60000; // UTC-4 en milisegundos
  return new Date(utcTime + drOffset);
};

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD para inputs de tipo date
 * Usa la zona horaria de República Dominicana (UTC-4)
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const getLocalDateString = () => {
  const drDate = getDateInDR();
  const year = drDate.getFullYear();
  const month = String(drDate.getMonth() + 1).padStart(2, '0');
  const day = String(drDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formatea una fecha al formato de República Dominicana: dd/mm/yyyy
 * @param {string|Date} dateValue - Fecha a formatear
 * @returns {string} Fecha formateada: dd/mm/yyyy
 */
export const formatDateDR = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Formatea una fecha y hora al formato de República Dominicana: dd/mm/yyyy HH:MM a. m./p. m.
 * @param {string|Date} dateValue - Fecha a formatear
 * @returns {string} Fecha y hora formateada
 */
export const formatDateTimeDR = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return '';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
  hours = hours % 12;
  hours = hours ? hours : 12; // La hora 0 debe ser 12
  const hoursStr = String(hours).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
};

/**
 * =========================================
 * FUNCIONES DE MONEDA
 * =========================================
 */

/**
 * Format number to Dominican Republic currency format
 * Example: 1234567.89 -> RD$ 1,234,567.89
 * @param {number} value - The number to format
 * @param {boolean} includeCurrency - Whether to include RD$ prefix
 * @returns {string} Formatted number
 */
export const formatCurrency = (value, includeCurrency = true) => {
  if (value === null || value === undefined || isNaN(value)) {
    return includeCurrency ? 'RD$ 0.00' : '0.00';
  }
  
  const formatted = new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  
  return includeCurrency ? `RD$ ${formatted}` : formatted;
};

/**
 * Format number without currency symbol
 * Example: 1234567.89 -> 1,234,567.89
 * @param {number} value - The number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (value) => {
  return formatCurrency(value, false);
};
